
import * as _ from "lodash"
import {DataStore, Mapper, Schema, Record, LinkedCollection} from 'js-data';

import {ASync} from "../Basic/ASync";
import {ESDataStore} from "./ESDataStore";
import {ESRecord} from "./ESRecord";

// kvd
// We need to read and resolve relationships efficiently,
// removing the single record gets, and any repetitive has many gets..
// Records have three types of relationships
// 1. Has Many (Child Records)
// 2. Belongs to (Parent records)
// 3. HasOne (Single Child" record, similar to HasMany relationship)
//
//  These relationships are defined by the records Mapper Relationships..

export const Relation_BelongsTo = 'belongsTo';
export const Relation_HasMany = 'hasMany';
export const Relation_HasOne = 'hasOne';


export interface jsdRelation {
  type: string,
  localField: string,   // All Relation Types
  relation: string,        //
  relatedMapper?: Mapper,
  inverse?: jsdRelation,
  foreignKey?: string    // BelongsTo     = field on local row to primary key of related mapper
                         // HasMany/One   = field on foreign mapper field.

}

// Needs
//
// 1. Need static conditions by entity set
// 2. Ignore some HasManyRelationships
//
// maintain list of belongs to relationships to retrieve
// Recurse down HasMany relationships first, then resolve belongs to..& HasOne
// when you can't recurse down anymore
// After each find all,
//     iterate over the belongsTo relatoinships, storing the keys to resolve.
//     iterate over the hasOne relationships, storing the keys to resolve.

interface relationshipData {
  bRowSearched: boolean,
  bRowResolved: boolean,
  bRowRelationshipsResolved: boolean,
  data: ESRecord | ESRecord[]
}

//
//  Belongs To
interface belongsToQueue {
  mapperName: string;
  dctRelationById: {[id: string]: relationshipData };
  bSearchProcessed: boolean;
}

interface belongsToByMapperDictionary {
  [mapperName: string]: belongsToQueue
}


//
// Has Many
interface hasManyQueue {
  mapperName: string;
  foreignKey: string;
  dctRelationById: {[id: string]: relationshipData };
  bSearchProcessed: boolean;
}

interface hasManyByForeignKeyDictionary {
  [foreignKey: string]: hasManyQueue
}

interface hasManyByMapperDictionary {
  [mapperName: string]: hasManyByForeignKeyDictionary
}

// Has Many
interface hasOneQueue {
  mapperName: string;
  foreignKey: string;
  dctRelationById: {[id: string]: relationshipData };
  bSearchProcessed: boolean;
}

interface hasOneByForeignKeyDictionary {
  [foreignKey: string]: hasOneQueue
}

interface hasOneByMapperDictionary {
  [mapperName: string]: hasOneByForeignKeyDictionary
}



export interface CreateOptions {
  standardConditions?: {[mapperName: string]: any },
  dctIncludeBelongsToNamedRelations?: {[id:string]: string[]},
  dctExcludeHasManyNamedRelations?: {[id:string]: string[]}
}

const CreateOptionsDefault: CreateOptions = {
  standardConditions: {}
}

export interface FindAllOptions {
  standardConditions?: {},
  belongToIncludes?: string[]

}


export interface CollectRelationshipOptions {
  bIncludeBelongsTo?: boolean,
  dctIncludeBelongsToNamedRelations?: {[id:string]: string[]},
  dctExcludeHasManyNamedRelations?: {[id:string]: string[]},
  bIncludeHasMany?: boolean,
  bIncludeHasOne?: boolean
}

const CollectRelationshipOptionsDefault: CollectRelationshipOptions = {
  bIncludeBelongsTo: true,
  dctIncludeBelongsToNamedRelations: {},
  dctExcludeHasManyNamedRelations: {},
  bIncludeHasMany: true,
  bIncludeHasOne: true
}


export class ESRecordResolver {

  private ds: ESDataStore;
  private createOptions: CreateOptions;

  private dctBelongsToByMapper: belongsToByMapperDictionary
  private dctHasManyByMapper: hasManyByMapperDictionary
  private dctHasOneByMapper: hasOneByMapperDictionary



  constructor(pDS: ESDataStore, options?: CreateOptions) {
    this.ds = pDS
    this.dctBelongsToByMapper = {}
    this.dctHasManyByMapper = {}
    this.dctHasOneByMapper = {}
    this.createOptions = {}
    _.defaults(this.createOptions, options || {}, CreateOptionsDefault);

  }

  // Steps
  //   1. Query for results and set found/ searched
  //   2. Loop Resolve HasMany until we can't anymore.

  findAllWithRelationships(mapperName: string, arrIds: (string | number)[], options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this

    return new Promise<ESRecord[]>((resolve, error) => {
      //  Get the initial record set
      self.fetchBelongsToRecords(mapperName, arrIds, options)
        .then((results) => {
          return self.processAllRelationships(mapperName, results, options)
        }).then((processed_results) => {
          resolve(processed_results)
        }).catch((err) => {
          error(err);
        })
    })
  }

  queryAllWithRelationships(mapperName: string, findQuery: {[id: string]: any}, options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this

    return new Promise<ESRecord[]>((resolve, error) => {
      //  Get the initial record set
      self.queryBelongsToRecords(mapperName, findQuery, options)
        .then((results) => {
          return self.processAllRelationships(mapperName, results, options)
        }).then((processed_results) => {
        resolve(processed_results)
      }).catch((err) => {
        error(err);
      })
    })
  }


  private processAllRelationships(mapperName: string, records: ESRecord[], options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this

    return new Promise<ESRecord[]>((resolve, error) => {

      //  Process the initial record set
      let collectOpts: any = {}
      _.defaultsDeep(collectOpts, self.createOptions)
      self.collectRelationships(mapperName, records, collectOpts);

      async function resolveRelationships() {
        try {
          while (true) {
            // 1. Fetch has Many Relationships..
            //  --  Collecting relationships as we go
            //
            await Promise.all([self.resolveHasManyRelationships(),self.resolveHasOneRelationships() ])

            if (!(self.bContinueHasManyRelationshipSearch || self.bContinueHasOneRelationshipSearch)) {
              break;
            }
          }

          // Resolve Belongs To Relationships
          await self.resolveBelongsToRelationships()

          if ((self.bContinueHasManyRelationshipSearch || self.bContinueHasOneRelationshipSearch)) {
            await Promise.all([ self.resolveHasManyRelationships(), self.resolveHasOneRelationships()]);
          }

          await self.resolveBelongsToRelationships()

          // Do this again for Grand Parents
          await self.resolveBelongsToRelationships()

          self.setRelationshipFields();

        } catch (err) {
          error(err)
        }

        resolve(records);
        return;
      }

      resolveRelationships();
    })

  }




  get(mapperName: string, anId: string | number): ESRecord {
    if(mapperName in this.dctBelongsToByMapper){
      let btQ = this.dctBelongsToByMapper[mapperName]
      if(anId in btQ.dctRelationById){
        let relD = btQ.dctRelationById[anId]
        if(!_.isEmpty(relD.data)) {
          return <ESRecord> relD.data
        }
      }
    }
    return undefined
  }


  private async resolveHasManyRelationships(): Promise<void> {
    const self = this

        //
        let arrHasManyQueues: hasManyQueue[] = []
        _.forEach(self.dctHasManyByMapper, (value1, key1) => {
          _.forEach(value1, (aHmQ, key2) => {
            //
            if(!aHmQ.bSearchProcessed) {
              aHmQ.bSearchProcessed = true
              arrHasManyQueues.push(aHmQ)
            }
          })
        })

        let tasks: Promise<any>[] = []
        for (let idx = 0; idx < arrHasManyQueues.length; idx++) {
          let hmQ = arrHasManyQueues[idx]
          let arrIds: any[] = [] //_.keys(hmQ.dctRelationById)
          _.forEach(hmQ.dctRelationById, (value, key) => {
            if(!value.bRowSearched){
              arrIds.push(key)
              value.bRowSearched = true
            }
          })

          tasks.push(self.fetchHasManyRecords(hmQ.mapperName, hmQ.foreignKey, arrIds)
            .then((hmRows) => {
              let collectOpts:any = {}
              _.defaultsDeep(collectOpts, self.createOptions)
              self.collectRelationships(hmQ.mapperName, hmRows, collectOpts)
            }))
        }

        if(tasks.length){
          await Promise.all(tasks)
        }

  }

  private get bContinueHasManyRelationshipSearch(): boolean {

    let returnValue = false;
    _.forEach(this.dctHasManyByMapper, (value1, key1) => {
      _.forEach(value1, (aHmQ, key2) => {
        //
        if(!aHmQ.bSearchProcessed) {
          returnValue = true;
          return false;  // stop iterating
        }
      })
      if(returnValue) { return false } // stop iterating
    })

    return returnValue;
  }

  private async resolveHasOneRelationships(): Promise<void> {
    const self = this

        //
        let arrHasOneQueues: hasOneQueue[] = []
        _.forEach(self.dctHasOneByMapper, (value1, key1) => {
          _.forEach(value1, (aHoQ, key2) => {
            //
            if(!aHoQ.bSearchProcessed) {
              aHoQ.bSearchProcessed = true
              arrHasOneQueues.push(aHoQ)
            }
          })
        })

        let tasks: Promise<any>[] = []
        for (let idx = 0; idx < arrHasOneQueues.length; idx++) {
          let hoQ = arrHasOneQueues[idx]
          let arrIds: any[] = [] //_.keys(hoQ.dctRelationById)
          _.forEach(hoQ.dctRelationById, (value, key) => {
            if(!value.bRowSearched){
              arrIds.push(key)
              value.bRowSearched = true
            }
          })
          tasks.push(
            self.fetchHasOneRecords(hoQ.mapperName, hoQ.foreignKey, arrIds)
              .then((hoRows) => {
                let collectOpts:any = {}
                _.defaultsDeep(collectOpts, self.createOptions)
                self.collectRelationships(hoQ.mapperName, hoRows, collectOpts)
              }))

        }

        if(tasks.length){
          await Promise.all(tasks)
        }

  }

  private get bContinueHasOneRelationshipSearch(): boolean {

    let returnValue = false;
    _.forEach(this.dctHasOneByMapper, (value1, key1) => {
      _.forEach(value1, (aHoQ, key2) => {
        //
        if(!aHoQ.bSearchProcessed) {
          returnValue = true;
          return false; // stop iterating
        }
      })
      if(returnValue) { return false } // stop iterating
    })

    return returnValue;
  }

  private async resolveBelongsToRelationships(): Promise<void> {
    const self = this

    //
    let arrBelongsToQueues: belongsToQueue[] = []
    _.forEach(self.dctBelongsToByMapper, (aBtQ, key1) => {
      if (!aBtQ.bSearchProcessed) {
        aBtQ.bSearchProcessed = true
        arrBelongsToQueues.push(aBtQ)
      }
    })

    let tasks: Promise<any>[] = []
    for (let idx = 0; idx < arrBelongsToQueues.length; idx++) {
      let btQ = arrBelongsToQueues[idx]
      let arrIds: any[] = [] //_.keys(hoQ.dctRelationById)
      _.forEach(btQ.dctRelationById, (value, key) => {
        if (!value.bRowSearched) {
          arrIds.push(key)
          value.bRowSearched = true
        }
      })

      tasks.push(self.fetchBelongsToRecords(btQ.mapperName, arrIds)
        .then((btRows) => {
          let collectionOptions: any = {bIncludeHasMany: false}
          _.defaultsDeep(collectionOptions, self.createOptions)
          self.collectRelationships(btQ.mapperName, btRows, collectionOptions)
        }))
    }

    if(tasks.length){
      await Promise.all(tasks)
    }

  }


  private setRelationshipFields() {

    // Iterate over Belong to Dictionary.. collect rows..
    _.forEach(this.dctBelongsToByMapper, (btQ: belongsToQueue, mapperName: string) => {
      // Create collection of rows
      let mapperRows: any[] = []
      _.forEach(btQ.dctRelationById, (relD: relationshipData, id: string) => {
        if (!_.isEmpty(relD.data)) {
          if(!relD.bRowRelationshipsResolved) {
            relD.bRowRelationshipsResolved = true
            mapperRows.push(relD.data)
          }
        }
      })

      if(_.isEmpty(mapperRows)){
        return;
      }

      var mapper = this.ds.getMapperByName(mapperName)
      if (_.isEmpty(mapper)) {
        return
      }

      _.forEach(mapper.relationList, (rel: jsdRelation) => {

        switch (rel.type) {
          case Relation_BelongsTo:
            this.setBelongsToProperty(rel, mapperRows)
            break;
        }
      })

    })
  }

  private setBelongsToProperty(rel: jsdRelation, records: ESRecord[]) {
    // Get dictionary of Rows
    if(!(rel.relation in this.dctBelongsToByMapper)){
      return
    }

    let tgtBTQ = this.dctBelongsToByMapper[rel.relation];
    let dctById = tgtBTQ.dctRelationById

    _.forEach(records, (aRecord: ESRecord) => {
      let rowfk = (<any>aRecord)[rel.foreignKey]
      if (rowfk in tgtBTQ.dctRelationById) {
         let tgt = tgtBTQ.dctRelationById[rowfk];
         if (!_.isEmpty(tgt.data)) {
           // Avoid indexing re-work, if js-data has done it already
           // @ts-ignore
           if(aRecord[rel.localField] !== tgt.data)
           (<any> aRecord)[rel.localField] = tgt.data
         }
      }
    })

  }






  private fetchBelongsToRecords(mapperName: string, arrIds: (string | number)[], options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this


    if(_.isEmpty(arrIds)) {
      return ASync.resolve([]);
    }

    let mapper: Mapper = this.ds.getMapperByName(mapperName);

    return new Promise<ESRecord[]>((resolve, error) => {

      self.ds.findUsingCache(mapperName, <string[]> arrIds, {})
        .then((results: ESRecord[]) => {


          self.processBelongsToRecords(mapperName, results, arrIds, options)



          resolve(results);

        }).catch((err) => {
          error(err);
        })

    })


  }


  private queryBelongsToRecords(mapperName: string, findQuery: {[field: string]: any}, options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this


    let mapper: Mapper = this.ds.getMapperByName(mapperName);

    return new Promise<ESRecord[]>((resolve, error) => {

      self.ds.findAll(mapperName, findQuery, {})
        .then((results: ESRecord[]) => {

          self.processBelongsToRecords(mapperName, results, undefined, options)

          resolve(results);

        }).catch((err) => {
        error(err);
      })

    })


  }


  private processBelongsToRecords(mapperName: string, records: ESRecord[], arrIds?: (string | number)[], options?: FindAllOptions) {
    //
    const self = this

    if (_.isEmpty(records)) {
      return;
    }

    let mapper: Mapper = this.ds.getMapperByName(mapperName);

    // Iterate over the records and add BelongsToResolution

    if (!_.isEmpty(arrIds)) {  // Use Id array to record searched records that may be missing in DB
      let dctById: any = _.keyBy(records, (aRow: ESRecord) => {
        return (<any>aRow)[mapper.idAttribute]
      })
      _.forEach(arrIds, (anId: string | number) => {
        self.setBelongToReference(mapperName, anId, dctById[anId])
      })
    } else {
      _.forEach(records, (aRecord: ESRecord) => {
        self.setBelongToReference(mapperName, (<any> aRecord)[mapper.idAttribute], aRecord)
      })
    }

  }


  private fetchHasManyRecords(mapperName: string, foreignKey: string, arrIds: (string | number)[], options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this
    const opts: FindAllOptions = options || {}
    _.defaultsDeep(opts, this.createOptions)

    if (_.isEmpty(arrIds)) {
      return ASync.resolve([]);
    }

    let mapper: Mapper = this.ds.getMapperByName(mapperName);

    return new Promise<ESRecord[]>((resolve, error) => {
      // Run Query
      // Create Query
      let queryConditions: any = {}
      if (mapperName in opts.standardConditions) {
        queryConditions  = (<CreateOptions> opts).standardConditions[mapperName];
      }

      //queryConditions[foreignKey] = {inq: arrIds}

      self.ds.findAllInChunks(mapperName, {where: queryConditions}, foreignKey,arrIds, {})
        .then((results: ESRecord[]) => {

          // First add each row as resolved belongsTo result
          _.forEach(results, (aRow: ESRecord) => {
            let rowPK: number | string = (<any> aRow)[mapper.idAttribute];
            self.setBelongToReference(mapperName, rowPK, aRow);
          })

          // Second, set hasMany Relationships
          let dctByFK = _.groupBy(results, foreignKey)

          _.forEach(arrIds, (anId) => {
            self.setHasManyReference(mapperName, foreignKey, anId, dctByFK[anId])
          })

          resolve(results)

        }).catch((err) => {
        error(err);
      })


    });

  }

  private fetchHasOneRecords(mapperName: string, foreignKey: string, arrIds: (string | number)[], options?: FindAllOptions): Promise<ESRecord[]> {
    //
    const self = this
    const opts: FindAllOptions = options || {}
    _.defaultsDeep(opts, this.createOptions)

    if (_.isEmpty(arrIds)) {
      return ASync.resolve([]);
    }

    let mapper: Mapper = this.ds.getMapperByName(mapperName);

    return new Promise<ESRecord[]>((resolve, error) => {
      // Run Query
      // Create Query
      let queryConditions: any = {}
      if (mapperName in opts.standardConditions) {
        queryConditions  = (<CreateOptions> opts).standardConditions[mapperName];
      }

      // Split up these calls.. the latency is very high when there are a large number of foreign keys.
      self.ds.findAllInChunks(mapperName, {where: queryConditions}, foreignKey , arrIds,{})
        .then((results: ESRecord[]) => {

          // First add each row as resolved belongsTo result
          _.forEach(results, (aRow: ESRecord) => {
            let rowPK: number | string = (<any> aRow)[mapper.idAttribute];
            self.setBelongToReference(mapperName, rowPK, aRow);
          })

          // Second, set hasMany Relationships
          let dctByFK = _.keyBy(results, foreignKey)

          _.forEach(arrIds, (anId) => {
            self.setHasOneReference(mapperName, foreignKey, anId, dctByFK[anId])
          })

          resolve(results)

        })
        .catch((err) => {
          error(err);
        })


    });

  }

  private collectRelationships(esMapperName: string,
                               mapperRecords: ESRecord[],
                               options?: CollectRelationshipOptions) {

    const opts: CollectRelationshipOptions = options || {}
    _.defaults(opts, CollectRelationshipOptionsDefault)

    if(_.isEmpty(mapperRecords)) {
      return;
    }

    let mapper  = this.ds.getMapperByName(esMapperName);
    if(_.isEmpty(mapper)){ return }

    let arrIncludeRelations: string[] = []
    let arrExcludeHasManyRelations: string[] = []

    if(esMapperName in opts.dctIncludeBelongsToNamedRelations) {
      arrIncludeRelations = opts.dctIncludeBelongsToNamedRelations[esMapperName];
    }

    if(esMapperName in opts.dctExcludeHasManyNamedRelations) {
      arrExcludeHasManyRelations = opts.dctExcludeHasManyNamedRelations[esMapperName];
    }


    _.forEach(mapperRecords, (anESRecord: ESRecord)=> {
      //
      if(_.isEmpty(anESRecord)){ return }

      let rowPK: string | number = (<any> anESRecord)[mapper.idAttribute];


      _.forEach(mapper.relationList, (rel: jsdRelation) => {

            switch(rel.type) {
              case Relation_BelongsTo:
                if (opts.bIncludeBelongsTo || _.includes(arrIncludeRelations, rel.relation) ) {
                  let localFK: string | number = (<any>anESRecord)[rel.foreignKey];
                  this.collectBelongsToReference(rel.relation, localFK);
                }
                break;

              case Relation_HasMany:
                let bInIncludeList = _.includes(arrIncludeRelations, rel.relation)
                if(bInIncludeList) {
                  this.collectHasManyReference(rel.relation, rel.foreignKey, rowPK);
                  break;
                }

                if (opts.bIncludeHasMany) {
                  if(!_.includes(arrExcludeHasManyRelations, rel.relation) && !_.includes(arrExcludeHasManyRelations, 'All'))
                    this.collectHasManyReference(rel.relation, rel.foreignKey, rowPK);

                }
                break;

              case Relation_HasOne:
                if (opts.bIncludeHasOne || _.includes(arrIncludeRelations, rel.relation)) {
                  this.collectHasOneReference(rel.relation, rel.foreignKey, rowPK);
                }
                break;
            }

      })

    })

  }

  private collectBelongsToReference(mapperName: string, id: string | number) {

    if(_.isUndefined(id)) { return }

    let btQ: belongsToQueue

    if( mapperName in this.dctBelongsToByMapper) {
      btQ = this.dctBelongsToByMapper[mapperName];
    } else {
      btQ = { mapperName: mapperName,
              dctRelationById: {},
              bSearchProcessed: false
            }

      this.dctBelongsToByMapper[mapperName] = btQ
    }

    if(!(id in btQ.dctRelationById)) {
      btQ.dctRelationById[id] = { bRowSearched: false, bRowResolved: false, bRowRelationshipsResolved: false,  data: undefined }
      btQ.bSearchProcessed = false
    }

  }

  private collectHasManyReference(mapperName: string, foreignKey: string, id: string | number) {

    if(_.isUndefined(id)) { return }

    let hmQ: hasManyQueue
    let dctHasManyByFK: hasManyByForeignKeyDictionary

    if( mapperName in this.dctHasManyByMapper) {
      dctHasManyByFK = this.dctHasManyByMapper[mapperName];
    } else {
      dctHasManyByFK = {};
      this.dctHasManyByMapper[mapperName] = dctHasManyByFK
    }

    if (foreignKey in dctHasManyByFK) {
      hmQ = dctHasManyByFK[foreignKey];
    } else {
      hmQ = { mapperName: mapperName,
              foreignKey: foreignKey,
              dctRelationById: {},
              bSearchProcessed: false
             }
      dctHasManyByFK[foreignKey] = hmQ;
    }

    if(!(id in hmQ.dctRelationById)) {
      hmQ.dctRelationById[id] = { bRowSearched: false, bRowResolved: false, bRowRelationshipsResolved: false,  data: [] }
      hmQ.bSearchProcessed = false
    }

  }

  private collectHasOneReference(mapperName: string, foreignKey: string, id: string | number) {

    if(_.isUndefined(id)) { return }

    let hoQ: hasOneQueue
    let dctHasOneByFK: hasOneByForeignKeyDictionary

    if( mapperName in this.dctHasOneByMapper) {
      dctHasOneByFK = this.dctHasOneByMapper[mapperName];
    } else {
      dctHasOneByFK = {};
      this.dctHasOneByMapper[mapperName] = dctHasOneByFK
    }

    if (foreignKey in dctHasOneByFK) {
      hoQ = dctHasOneByFK[foreignKey];
    } else {
      hoQ = { mapperName: mapperName,
              foreignKey: foreignKey,
              dctRelationById: {},
              bSearchProcessed: false
            }
      dctHasOneByFK[foreignKey] = hoQ;
    }

    if(!(id in hoQ.dctRelationById)) {
      hoQ.dctRelationById[id] =  { bRowSearched: false, bRowResolved: false, bRowRelationshipsResolved: false,  data: [] }
      hoQ.bSearchProcessed = false
    }

  }

  private setBelongToReference(mapperName: string, id: string | number, row: ESRecord) {
    let btQ: belongsToQueue

    if( mapperName in this.dctBelongsToByMapper) {
      btQ = this.dctBelongsToByMapper[mapperName];
    } else {
      btQ = { mapperName: mapperName,
        dctRelationById: {},
        bSearchProcessed: false
      }

      this.dctBelongsToByMapper[mapperName] = btQ
    }

    if(!(id in btQ.dctRelationById)) {
      btQ.dctRelationById[id] = { bRowSearched: true, bRowResolved: true, bRowRelationshipsResolved: false,  data: row }
    } else {
      let rel = btQ.dctRelationById[id]
      rel.bRowSearched = true
      rel.bRowResolved = true
      rel.data =row
      //rel.bRowRelationshipsResolved don't change
    }

  }

  private setHasManyReference(mapperName: string, foreignKey: string, id: string | number, rows: ESRecord[]) {
    let hmQ: hasManyQueue

    let dctHasManyByFK: hasManyByForeignKeyDictionary

    if( mapperName in this.dctHasManyByMapper) {
      dctHasManyByFK = this.dctHasManyByMapper[mapperName];
    } else {
      dctHasManyByFK = {};
      this.dctHasManyByMapper[mapperName] = dctHasManyByFK
    }

    if (foreignKey in dctHasManyByFK) {
      hmQ = dctHasManyByFK[foreignKey];
    } else {
      hmQ = { mapperName: mapperName,
        foreignKey: foreignKey,
        dctRelationById: {},
        bSearchProcessed: false
      }
      dctHasManyByFK[foreignKey] = hmQ;
    }

    if(!(id in hmQ.dctRelationById)) {
      hmQ.dctRelationById[id] = { bRowSearched: true, bRowResolved: true, bRowRelationshipsResolved: false,  data: rows }
    } else {
      let rel = hmQ.dctRelationById[id]
      rel.bRowSearched = true
      rel.bRowResolved = true
      rel.data = rows
      //rel.bRowRelationshipsResolved don't change
    }

  }

  private setHasOneReference(mapperName: string, foreignKey: string, id: string | number, row: ESRecord) {
    let hoQ: hasOneQueue

    let dctHasOneByFK: hasOneByForeignKeyDictionary

    if( mapperName in this.dctHasOneByMapper) {
      dctHasOneByFK = this.dctHasOneByMapper[mapperName];
    } else {
      dctHasOneByFK = {};
      this.dctHasOneByMapper[mapperName] = dctHasOneByFK
    }

    if (foreignKey in dctHasOneByFK) {
      hoQ = dctHasOneByFK[foreignKey];
    } else {
      hoQ = { mapperName: mapperName,
        foreignKey: foreignKey,
        dctRelationById: {},
        bSearchProcessed: false
      }
      dctHasOneByFK[foreignKey] = hoQ;
    }

    if(!(id in hoQ.dctRelationById)) {
      hoQ.dctRelationById[id] = { bRowSearched: true, bRowResolved: true, bRowRelationshipsResolved: false,  data: row }
    } else {
      let rel = hoQ.dctRelationById[id]
      rel.bRowSearched = true
      rel.bRowResolved = true
      rel.data = row
      //rel.bRowRelationshipsResolved don't change
    }

  }




}














